import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import { supabase } from '../services/supabaseClient';

const AdminProveedores = ({ user }) => {
  // Estados principales
  const [servicios, setServicios] = useState([]);
  const [estadisticas, setEstadisticas] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para tabs
  const [activeTab, setActiveTab] = useState('pendientes');
  
  // Estados para modales
  const [modalDetalle, setModalDetalle] = useState(false);
  const [modalRechazar, setModalRechazar] = useState(false);
  const [servicioSeleccionado, setServicioSeleccionado] = useState(null);
  const [comentarioRechazo, setComentarioRechazo] = useState('');

  // Categorías para mostrar información
  const categorias = {
    'construccion': { nombre: 'Construcción & Obras', icono: '🏗️', color: '#f39c12' },
    'consultoria': { nombre: 'Consultoría', icono: '📊', color: '#3498db' },
    'tecnologia': { nombre: 'Tecnología & Software', icono: '💻', color: '#9b59b6' },
    'servicios_generales': { nombre: 'Servicios Generales', icono: '🔧', color: '#e74c3c' },
    'suministros': { nombre: 'Suministros & Materiales', icono: '📦', color: '#27ae60' },
    'salud': { nombre: 'Salud & Medicina', icono: '🏥', color: '#e67e22' },
    'educacion': { nombre: 'Educación & Capacitación', icono: '📚', color: '#2c3e50' },
    'transporte': { nombre: 'Transporte & Logística', icono: '🚛', color: '#34495e' },
    'seguridad': { nombre: 'Seguridad & Vigilancia', icono: '🛡️', color: '#8e44ad' },
    'limpieza': { nombre: 'Limpieza & Mantenimiento', icono: '🧹', color: '#16a085' },
    'otros': { nombre: 'Otros Servicios', icono: '⭐', color: '#6c757d' }
  };

  useEffect(() => {
    // CORRECCIÓN: Cambiar user?.plan por user?.role
    if (user?.email !== 'ADMI') {
      setError('Solo los administradores pueden acceder a esta sección');
      setLoading(false);
      return;
    }

    loadData();
  }, [user, activeTab]);

  // FUNCIÓN CORREGIDA: Cargar datos directamente desde Supabase
  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Cargar todos los servicios para estadísticas
      const { data: todosLosServicios, error: errorTodos } = await supabase
        .from('servicios_proveedores')
        .select('*')
        .order('created_at', { ascending: false });

      if (errorTodos) throw errorTodos;

      // Procesar especialidades
      const serviciosProcesados = todosLosServicios.map(servicio => ({
        ...servicio,
        especialidades: typeof servicio.especialidades === 'string' 
          ? servicio.especialidades.split(',').map(s => s.trim()).filter(s => s)
          : servicio.especialidades || []
      }));

      setServicios(serviciosProcesados);

      // Calcular estadísticas
      const stats = {
        total: serviciosProcesados.length,
        pendientes: serviciosProcesados.filter(s => s.estado === 'pendiente').length,
        aprobados: serviciosProcesados.filter(s => s.estado === 'aprobado').length,
        rechazados: serviciosProcesados.filter(s => s.estado === 'rechazado').length
      };

      setEstadisticas(stats);

    } catch (error) {
      console.error('Error cargando datos:', error);
      setError('Error al cargar los datos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // FUNCIÓN CORREGIDA: Aprobar servicio
  const handleAprobar = async (servicio) => {
    const confirmacion = window.confirm(
      `¿Aprobar el servicio "${servicio.titulo}" de ${servicio.empresa}?`
    );
    
    if (!confirmacion) return;

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('servicios_proveedores')
        .update({ 
          estado: 'aprobado',
          fecha_aprobacion: new Date().toISOString(),
          aprobado_por: user.email
        })
        .eq('id', servicio.id);

      if (error) throw error;
      
      alert('✅ Servicio aprobado exitosamente');
      loadData(); // Recargar datos
    } catch (error) {
      console.error('Error aprobando servicio:', error);
      alert('❌ Error al aprobar el servicio: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // FUNCIÓN CORREGIDA: Rechazar servicio
  const handleRechazar = async () => {
    if (!comentarioRechazo.trim()) {
      alert('Por favor, proporciona un motivo para el rechazo');
      return;
    }

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('servicios_proveedores')
        .update({ 
          estado: 'rechazado',
          fecha_rechazo: new Date().toISOString(),
          rechazado_por: user.email,
          motivo_rechazo: comentarioRechazo
        })
        .eq('id', servicioSeleccionado.id);

      if (error) throw error;
      
      alert('❌ Servicio rechazado exitosamente');
      setModalRechazar(false);
      setComentarioRechazo('');
      setServicioSeleccionado(null);
      loadData(); // Recargar datos
    } catch (error) {
      console.error('Error rechazando servicio:', error);
      alert('❌ Error al rechazar el servicio: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // FUNCIÓN NUEVA: Eliminar servicio
  const handleEliminarServicio = async (servicioId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este servicio? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('servicios_proveedores')
        .delete()
        .eq('id', servicioId);

      if (error) throw error;

      alert('✅ Servicio eliminado exitosamente');
      loadData(); // Recargar datos
    } catch (error) {
      console.error('Error eliminando servicio:', error);
      alert('❌ Error al eliminar el servicio: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCategoriaInfo = (categoriaId, categoriaPersonalizada = null) => {
    if (categoriaId === 'otros' && categoriaPersonalizada) {
      return {
        nombre: categoriaPersonalizada,
        icono: '⭐',
        color: '#6c757d'
      };
    }
    return categorias[categoriaId] || { nombre: 'Sin categoría', icono: '❓', color: '#6c757d' };
  };

  // Filtrar servicios según el tab activo
  const serviciosFiltrados = servicios.filter(servicio => {
    switch (activeTab) {
      case 'pendientes':
        return servicio.estado === 'pendiente';
      case 'aprobados':
        return servicio.estado === 'aprobado';
      case 'rechazados':
        return servicio.estado === 'rechazado';
      default:
        return true;
    }
  });

  // CORRECCIÓN: Cambiar user?.plan por user?.role
  if (user?.role !== 'ADMI') {
    return (
      <div className="container-fluid p-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card border-0 shadow-lg">
              <div className="card-body text-center py-5">
                <div className="display-1 mb-4">🔒</div>
                <h3 className="text-danger mb-3">Acceso Restringido</h3>
                <p className="text-muted mb-4">
                  Solo los administradores pueden gestionar las solicitudes de proveedores.
                </p>
                <button 
                  className="btn btn-primary"
                  onClick={() => window.history.back()}
                >
                  ← Volver
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Card className="shadow-lg border-0 mb-4" style={{ borderRadius: '20px', background: 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)' }}>
        <CardBody className="p-4">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center mb-2">
                <div className="me-3" style={{ fontSize: '3rem' }}>
                  🛡️
                </div>
                <div>
                  <h1 className="mb-1 fw-bold text-white" style={{ fontSize: '2.5rem' }}>
                    Gestión de Proveedores
                  </h1>
                  <p className="mb-0 text-white-50" style={{ fontSize: '1.1rem' }}>
                    Panel de administración para aprobar solicitudes de servicios
                  </p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <div className="text-white">
                <div className="d-flex justify-content-end gap-3">
                  <div className="text-center">
                    <h3 className="mb-0 fw-bold">{estadisticas.pendientes || 0}</h3>
                    <small>Pendientes</small>
                  </div>
                  <div className="text-center">
                    <h3 className="mb-0 fw-bold">{estadisticas.aprobados || 0}</h3>
                    <small>Aprobados</small>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Estadísticas */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardBody className="text-white text-center py-4">
              <div className="display-4 mb-2">📋</div>
              <h2 className="fw-bold">{estadisticas.total || 0}</h2>
              <p className="mb-0 opacity-75">Total Servicios</p>
            </CardBody>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
            <CardBody className="text-white text-center py-4">
              <div className="display-4 mb-2">⏳</div>
              <h2 className="fw-bold">{estadisticas.pendientes || 0}</h2>
              <p className="mb-0 opacity-75">Pendientes</p>
            </CardBody>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
            <CardBody className="text-white text-center py-4">
              <div className="display-4 mb-2">✅</div>
              <h2 className="fw-bold">{estadisticas.aprobados || 0}</h2>
              <p className="mb-0 opacity-75">Aprobados</p>
            </CardBody>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)' }}>
            <CardBody className="text-white text-center py-4">
              <div className="display-4 mb-2">❌</div>
              <h2 className="fw-bold">{estadisticas.rechazados || 0}</h2>
              <p className="mb-0 opacity-75">Rechazados</p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Navegación Mejorada */}
      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: '16px' }}>
        <CardBody className="p-0">
          <Nav tabs className="border-0">
            <NavItem>
              <NavLink
                className={activeTab === 'pendientes' ? 'active fw-bold' : 'fw-bold'}
                onClick={() => setActiveTab('pendientes')}
                style={{ 
                  cursor: 'pointer',
                  borderRadius: '16px 0 0 0',
                  backgroundColor: activeTab === 'pendientes' ? '#f8f9fa' : 'transparent'
                }}
              >
                ⏳ Solicitudes Pendientes ({estadisticas.pendientes || 0})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'aprobados' ? 'active fw-bold' : 'fw-bold'}
                onClick={() => setActiveTab('aprobados')}
                style={{ 
                  cursor: 'pointer',
                  backgroundColor: activeTab === 'aprobados' ? '#f8f9fa' : 'transparent'
                }}
              >
                ✅ Servicios Aprobados ({estadisticas.aprobados || 0})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'rechazados' ? 'active fw-bold' : 'fw-bold'}
                onClick={() => setActiveTab('rechazados')}
                style={{ 
                  cursor: 'pointer',
                  backgroundColor: activeTab === 'rechazados' ? '#f8f9fa' : 'transparent'
                }}
              >
                ❌ Servicios Rechazados ({estadisticas.rechazados || 0})
              </NavLink>
            </NavItem>
          </Nav>
        </CardBody>
      </Card>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="pendientes">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <h5 className="mt-3">Cargando solicitudes...</h5>
            </div>
          ) : serviciosFiltrados.length > 0 ? (
            <Row>
              {serviciosFiltrados.map(servicio => {
                const categoriaInfo = getCategoriaInfo(servicio.categoria, servicio.categoria_personalizada);
                return (
                  <Col lg={6} className="mb-4" key={servicio.id}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
                      {/* Header */}
                      <div className="p-3" style={{ 
                        background: `linear-gradient(135deg, ${categoriaInfo.color}20 0%, ${categoriaInfo.color}40 100%)`,
                        borderRadius: '16px 16px 0 0'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <Badge 
                            style={{ 
                              backgroundColor: categoriaInfo.color,
                              fontSize: '11px',
                              padding: '6px 10px'
                            }}
                          >
                            {categoriaInfo.icono} {categoriaInfo.nombre}
                          </Badge>
                          <Badge color="warning" style={{ fontSize: '11px' }}>
                            ⏳ PENDIENTE
                          </Badge>
                        </div>
                      </div>

                      <CardBody className="p-4">
                        {/* Título y empresa */}
                        <h5 className="fw-bold mb-2 text-primary">{servicio.titulo}</h5>
                        <div className="d-flex align-items-center mb-3">
                          <span className="text-muted">🏢 {servicio.empresa}</span>
                        </div>

                        {/* Información del solicitante */}
                        <div className="mb-3 p-3 bg-light rounded">
                          <h6 className="fw-bold mb-2">👤 Solicitante</h6>
                          <div className="small">
                            <div><strong>Email:</strong> {servicio.usuario_email}</div>
                            <div><strong>RUC:</strong> {servicio.ruc}</div>
                          </div>
                        </div>

                        {/* Descripción */}
                        <p className="text-muted mb-3" style={{ 
                          lineHeight: '1.4',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {servicio.descripcion}
                        </p>

                        {/* Detalles del servicio */}
                        <div className="mb-3">
                          <div className="row small">
                            <div className="col-6">
                              <div><strong>📍 Ubicación:</strong> {servicio.ubicacion}</div>
                              <div><strong>📞 Teléfono:</strong> {servicio.telefono}</div>
                            </div>
                            <div className="col-6">
                              <div><strong>📧 Email:</strong> {servicio.email}</div>
                              <div><strong>⏱️ Experiencia:</strong> {servicio.experiencia || 'No especificada'}</div>
                            </div>
                          </div>
                        </div>

                        {/* Especialidades */}
                        {servicio.especialidades && servicio.especialidades.length > 0 && (
                          <div className="mb-3">
                            <strong className="small">🎯 Especialidades:</strong>
                            <div className="mt-1">
                              {servicio.especialidades.slice(0, 4).map((esp, index) => (
                                <Badge 
                                  key={index}
                                  color="outline-secondary" 
                                  className="me-1 mb-1"
                                  style={{ fontSize: '10px', padding: '3px 6px' }}
                                >
                                  {esp}
                                </Badge>
                              ))}
                              {servicio.especialidades.length > 4 && (
                                <Badge color="outline-secondary" style={{ fontSize: '10px' }}>
                                  +{servicio.especialidades.length - 4} más
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Fecha de solicitud */}
                        <div className="mb-3">
                          <small className="text-muted">
                            📅 Solicitado el {new Date(servicio.created_at).toLocaleString('es-PE')}
                          </small>
                        </div>

                        {/* Botones de acción CORREGIDOS */}
                        <div className="d-flex gap-2">
                          <Button
                            color="outline-info"
                            size="sm"
                            onClick={() => {
                              setServicioSeleccionado(servicio);
                              setModalDetalle(true);
                            }}
                            style={{ borderRadius: '8px', fontSize: '12px' }}
                          >
                            👁️ Ver Completo
                          </Button>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleAprobar(servicio)}
                            style={{ borderRadius: '8px', fontSize: '12px' }}
                          >
                            ✅ Aprobar
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => {
                              setServicioSeleccionado(servicio);
                              setModalRechazar(true);
                            }}
                            style={{ borderRadius: '8px', fontSize: '12px' }}
                          >
                            ❌ Rechazar
                          </Button>
                          {/* NUEVO: Botón eliminar */}
                          <Button
                            color="warning"
                            size="sm"
                            onClick={() => handleEliminarServicio(servicio.id)}
                            style={{ borderRadius: '8px', fontSize: '12px' }}
                          >
                            🗑️ Eliminar
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="text-center py-5">
              <div style={{ fontSize: '4rem', opacity: 0.3 }}>✅</div>
              <h4 className="text-muted mb-3">No hay solicitudes pendientes</h4>
              <p className="text-muted">Todas las solicitudes han sido revisadas</p>
            </div>
          )}
        </TabPane>

        {/* Tab para servicios aprobados */}
        <TabPane tabId="aprobados">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <h5 className="mt-3">Cargando servicios aprobados...</h5>
            </div>
          ) : serviciosFiltrados.length > 0 ? (
            <Row>
              {serviciosFiltrados.map(servicio => {
                const categoriaInfo = getCategoriaInfo(servicio.categoria, servicio.categoria_personalizada);
                return (
                  <Col lg={6} className="mb-4" key={servicio.id}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
                      <div className="p-3" style={{ 
                        background: `linear-gradient(135deg, #43e97b20 0%, #38f9d740 100%)`,
                        borderRadius: '16px 16px 0 0'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <Badge style={{ backgroundColor: categoriaInfo.color, fontSize: '11px', padding: '6px 10px' }}>
                            {categoriaInfo.icono} {categoriaInfo.nombre}
                          </Badge>
                          <Badge color="success" style={{ fontSize: '11px' }}>✅ APROBADO</Badge>
                        </div>
                      </div>
                      <CardBody className="p-4">
                        <h5 className="fw-bold mb-2 text-success">{servicio.titulo}</h5>
                        <div className="mb-3">
                          <span className="text-muted">🏢 {servicio.empresa}</span>
                        </div>
                        <p className="text-muted mb-3" style={{ 
                          lineHeight: '1.4',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {servicio.descripcion}
                        </p>
                        <div className="d-flex gap-2">
                          <Button
                            color="outline-info"
                            size="sm"
                            onClick={() => {
                              setServicioSeleccionado(servicio);
                              setModalDetalle(true);
                            }}
                          >
                            👁️ Ver Detalles
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleEliminarServicio(servicio.id)}
                          >
                            🗑️ Eliminar
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="text-center py-5">
              <div style={{ fontSize: '4rem', opacity: 0.3 }}>✅</div>
              <h4 className="text-muted mb-3">No hay servicios aprobados</h4>
            </div>
          )}
        </TabPane>

        {/* Tab para servicios rechazados */}
        <TabPane tabId="rechazados">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <h5 className="mt-3">Cargando servicios rechazados...</h5>
            </div>
          ) : serviciosFiltrados.length > 0 ? (
            <Row>
              {serviciosFiltrados.map(servicio => {
                const categoriaInfo = getCategoriaInfo(servicio.categoria, servicio.categoria_personalizada);
                return (
                  <Col lg={6} className="mb-4" key={servicio.id}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
                      <div className="p-3" style={{ 
                        background: `linear-gradient(135deg, #ff6b6b20 0%, #feca5740 100%)`,
                        borderRadius: '16px 16px 0 0'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <Badge style={{ backgroundColor: categoriaInfo.color, fontSize: '11px', padding: '6px 10px' }}>
                            {categoriaInfo.icono} {categoriaInfo.nombre}
                          </Badge>
                          <Badge color="danger" style={{ fontSize: '11px' }}>❌ RECHAZADO</Badge>
                        </div>
                      </div>
                      <CardBody className="p-4">
                        <h5 className="fw-bold mb-2 text-danger">{servicio.titulo}</h5>
                        <div className="mb-3">
                          <span className="text-muted">🏢 {servicio.empresa}</span>
                        </div>
                        {servicio.motivo_rechazo && (
                          <div className="mb-3 p-2 bg-light rounded">
                            <strong className="small text-danger">Motivo del rechazo:</strong>
                            <p className="small mb-0 text-muted">{servicio.motivo_rechazo}</p>
                          </div>
                        )}
                        <div className="d-flex gap-2">
                          <Button
                            color="outline-info"
                            size="sm"
                            onClick={() => {
                              setServicioSeleccionado(servicio);
                              setModalDetalle(true);
                            }}
                          >
                            👁️ Ver Detalles
                          </Button>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleAprobar(servicio)}
                          >
                            ✅ Aprobar
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleEliminarServicio(servicio.id)}
                          >
                            🗑️ Eliminar
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="text-center py-5">
              <div style={{ fontSize: '4rem', opacity: 0.3 }}>❌</div>
              <h4 className="text-muted mb-3">No hay servicios rechazados</h4>
            </div>
          )}
        </TabPane>
      </TabContent>

      {/* Modal para Ver Detalle Completo - CORREGIDO */}
      <Modal isOpen={modalDetalle} toggle={() => setModalDetalle(false)} size="lg">
        {servicioSeleccionado && (
          <>
            <ModalHeader toggle={() => setModalDetalle(false)} className="bg-info text-white">
              👁️ Detalle Completo de la Solicitud
            </ModalHeader>
            <ModalBody className="p-4">
              <Row>
                <Col md={4}>
                  <div className="text-center mb-4">
                    <div style={{ 
                      width: '100%', 
                      height: '150px',
                      background: `linear-gradient(135deg, ${getCategoriaInfo(servicioSeleccionado.categoria, servicioSeleccionado.categoria_personalizada).color}20 0%, ${getCategoriaInfo(servicioSeleccionado.categoria, servicioSeleccionado.categoria_personalizada).color}40 100%)`,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '3rem',
                      marginBottom: '1rem'
                    }}>
                      {getCategoriaInfo(servicioSeleccionado.categoria, servicioSeleccionado.categoria_personalizada).icono}
                    </div>
                    <Badge 
                      style={{ 
                        backgroundColor: getCategoriaInfo(servicioSeleccionado.categoria, servicioSeleccionado.categoria_personalizada).color,
                        fontSize: '12px',
                        padding: '8px 12px'
                      }}
                    >
                      {getCategoriaInfo(servicioSeleccionado.categoria, servicioSeleccionado.categoria_personalizada).nombre}
                    </Badge>
                  </div>

                  {/* Información del solicitante */}
                  <Card className="border-0 bg-light">
                    <CardBody className="p-3">
                      <h6 className="fw-bold mb-2">👤 Datos del Solicitante</h6>
                      <div className="small">
                        <div className="mb-1"><strong>Email:</strong> {servicioSeleccionado.usuario_email}</div>
                        <div className="mb-1"><strong>Empresa:</strong> {servicioSeleccionado.empresa}</div>
                        <div><strong>RUC:</strong> {servicioSeleccionado.ruc}</div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                
                <Col md={8}>
                  <h4 className="fw-bold text-primary mb-3">{servicioSeleccionado.titulo}</h4>
                  
                  <div className="mb-3">
                    <h6 className="fw-bold">📄 Descripción Completa</h6>
                    <p className="text-muted">{servicioSeleccionado.descripcion}</p>
                  </div>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <h6 className="fw-bold">📍 Ubicación</h6>
                        <p className="text-muted">{servicioSeleccionado.ubicacion}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <h6 className="fw-bold">⏱️ Experiencia</h6>
                        <p className="text-muted">{servicioSeleccionado.experiencia || 'No especificada'}</p>
                      </div>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <h6 className="fw-bold">📞 Información de Contacto</h6>
                    <div className="small">
                      <div><strong>Teléfono:</strong> {servicioSeleccionado.telefono}</div>
                      <div><strong>Email:</strong> {servicioSeleccionado.email}</div>
                      {servicioSeleccionado.whatsapp && (
                        <div><strong>WhatsApp:</strong> {servicioSeleccionado.whatsapp}</div>
                      )}
                      {servicioSeleccionado.website && (
                        <div><strong>Website:</strong> {servicioSeleccionado.website}</div>
                      )}
                    </div>
                  </div>

                  {servicioSeleccionado.especialidades && servicioSeleccionado.especialidades.length > 0 && (
                    <div className="mb-3">
                      <h6 className="fw-bold">🎯 Especialidades</h6>
                      <div>
                        {servicioSeleccionado.especialidades.map((esp, index) => (
                          <Badge 
                            key={index}
                            color="outline-primary" 
                            className="me-2 mb-1"
                            style={{ fontSize: '11px', padding: '6px 10px' }}
                          >
                            {esp}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mb-3">
                    <h6 className="fw-bold">📅 Fecha de Solicitud</h6>
                    <p className="text-muted">{new Date(servicioSeleccionado.created_at).toLocaleString('es-PE')}</p>
                  </div>

                  {/* Información de aprobación/rechazo */}
                  {servicioSeleccionado.estado === 'aprobado' && servicioSeleccionado.fecha_aprobacion && (
                    <div className="mb-3 p-3 bg-success bg-opacity-10 rounded">
                      <h6 className="fw-bold text-success">✅ Información de Aprobación</h6>
                      <div className="small">
                        <div><strong>Aprobado por:</strong> {servicioSeleccionado.aprobado_por}</div>
                        <div><strong>Fecha:</strong> {new Date(servicioSeleccionado.fecha_aprobacion).toLocaleString('es-PE')}</div>
                      </div>
                    </div>
                  )}

                  {servicioSeleccionado.estado === 'rechazado' && servicioSeleccionado.fecha_rechazo && (
                    <div className="mb-3 p-3 bg-danger bg-opacity-10 rounded">
                      <h6 className="fw-bold text-danger">❌ Información de Rechazo</h6>
                      <div className="small">
                        <div><strong>Rechazado por:</strong> {servicioSeleccionado.rechazado_por}</div>
                        <div><strong>Fecha:</strong> {new Date(servicioSeleccionado.fecha_rechazo).toLocaleString('es-PE')}</div>
                        {servicioSeleccionado.motivo_rechazo && (
                          <div><strong>Motivo:</strong> {servicioSeleccionado.motivo_rechazo}</div>
                        )}
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setModalDetalle(false)}>
                ❌ Cerrar
              </Button>
              {servicioSeleccionado.estado === 'pendiente' && (
                <>
                  <Button 
                    color="success" 
                    onClick={() => {
                      setModalDetalle(false);
                      handleAprobar(servicioSeleccionado);
                    }}
                  >
                    ✅ Aprobar Servicio
                  </Button>
                  <Button 
                    color="danger" 
                    onClick={() => {
                      setModalDetalle(false);
                      setModalRechazar(true);
                    }}
                  >
                    ❌ Rechazar Servicio
                  </Button>
                </>
              )}
              {servicioSeleccionado.estado === 'rechazado' && (
                <Button 
                  color="success" 
                  onClick={() => {
                    setModalDetalle(false);
                    handleAprobar(servicioSeleccionado);
                  }}
                >
                  ✅ Aprobar Servicio
                </Button>
              )}
              <Button 
                color="warning" 
                onClick={() => {
                  setModalDetalle(false);
                  handleEliminarServicio(servicioSeleccionado.id);
                }}
              >
                🗑️ Eliminar Servicio
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* Modal para Rechazar */}
      <Modal isOpen={modalRechazar} toggle={() => setModalRechazar(false)} size="md">
        {servicioSeleccionado && (
          <>
            <ModalHeader toggle={() => setModalRechazar(false)} className="bg-danger text-white">
              ❌ Rechazar Solicitud
            </ModalHeader>
            <Form onSubmit={(e) => { e.preventDefault(); handleRechazar(); }}>
              <ModalBody className="p-4">
                <Alert color="warning">
                  <h6 className="fw-bold">⚠️ Importante</h6>
                  <small>
                    Al rechazar esta solicitud, el proveedor recibirá una notificación con el motivo del rechazo.
                  </small>
                </Alert>

                <div className="mb-3">
                  <h6 className="fw-bold">📝 Servicio a Rechazar:</h6>
                  <p className="text-muted">{servicioSeleccionado.titulo}</p>
                  <p className="small text-muted">Por: {servicioSeleccionado.empresa}</p>
                </div>

                <FormGroup>
                  <Label className="fw-bold">💬 Motivo del Rechazo *</Label>
                  <Input
                    type="textarea"
                    rows="4"
                    value={comentarioRechazo}
                    onChange={(e) => setComentarioRechazo(e.target.value)}
                    placeholder="Explica el motivo del rechazo para que el proveedor pueda mejorar su solicitud..."
                    required
                    style={{ borderRadius: '8px' }}
                  />
                  <small className="text-muted">
                    Proporciona un motivo claro y constructivo para ayudar al proveedor.
                  </small>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={() => setModalRechazar(false)}>
                  ↩️ Cancelar
                </Button>
                <Button color="danger" type="submit" disabled={loading || !comentarioRechazo.trim()}>
                  {loading ? <Spinner size="sm" className="me-2" /> : '❌'} Rechazar Servicio
                </Button>
              </ModalFooter>
            </Form>
          </>
        )}
      </Modal>

      {error && (
        <Alert color="danger" className="mt-4">
          <h6 className="fw-bold">❌ Error</h6>
          {error}
        </Alert>
      )}
    </div>
  );
};

export default AdminProveedores;